import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import {
  AppointmentsIcon,
  EstimatesIcon,
  InvoiceIcon,
  JobsIcon,
  SearchIcon,
} from '../../assets/icons';

interface SearchEntity {
  key: string;
  label: string;
  icon: string;
  path: string;
}

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { guard } = useUnsavedChanges();
  const [query, setQuery] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const entities: SearchEntity[] = [
    {
      key: 'appointments',
      label: t('appointments'),
      icon: AppointmentsIcon,
      path: '/appointments',
    },
    { key: 'jobs', label: t('jobs'), icon: JobsIcon, path: '/jobs' },
    { key: 'estimates', label: t('estimates'), icon: EstimatesIcon, path: '/estimates' },
    { key: 'invoices', label: t('invoices'), icon: InvoiceIcon, path: '/invoices' },
  ];

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedEntity(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSearch = () => {
    if (!selectedEntity) return;
    const entity = entities.find(e => e.key === selectedEntity);
    if (!entity) return;

    const searchQuery = query.trim();
    const url = searchQuery
      ? `${entity.path}?search=${encodeURIComponent(searchQuery)}`
      : entity.path;
    // Close first, then ask. This modal sits at z-[9999]; closing it inside the
    // guarded action kept it open while the unsaved-changes dialog raised
    // underneath it, so the prompt was covered and could not be answered.
    // Picking a destination finishes the search either way — whether to leave
    // is the guard's question, not this modal's.
    onClose();
    // Through the guard, like the sidebar: this is reachable from a settings
    // card mid-edit, and it used to navigate straight out without a word.
    guard(() => navigate(url));
  };

  const handleEntitySelect = (entityKey: string) => {
    setSelectedEntity(entityKey);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (selectedEntity) {
        handleSearch();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="w-full max-w-[520px] bg-white rounded-ds-card shadow-2xl border border-ds-border overflow-hidden"
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-ds-border">
          <img
            src={SearchIcon}
            alt=""
            className="w-5 h-5 flex-shrink-0"
            style={{
              filter:
                'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1051%) hue-rotate(191deg) brightness(92%) contrast(86%)',
            }}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              selectedEntity
                ? t('searchIn', { entity: entities.find(e => e.key === selectedEntity)?.label })
                : t('searchEllipsis')
            }
            className="flex-1 text-sm text-ds-text-primary placeholder:text-ds-text-tertiary outline-none bg-transparent"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-ds-text-tertiary bg-ds-bg border border-ds-border rounded">
            ESC
          </kbd>
        </div>

        {/* Entity Selection */}
        <div className="p-2">
          {!selectedEntity && (
            <p className="px-3 py-1.5 text-xs font-medium text-ds-text-tertiary uppercase tracking-wider">
              {t('searchIn', { entity: '...' }).replace('...', '')}
            </p>
          )}

          {selectedEntity && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
              <button
                onClick={() => setSelectedEntity(null)}
                className="text-xs text-primary hover:text-primary/80 font-medium"
              >
                &larr; {t('allCategories')}
              </button>
              <span className="text-xs text-ds-text-tertiary">/</span>
              <span className="text-xs font-medium text-ds-text-primary">
                {entities.find(e => e.key === selectedEntity)?.label}
              </span>
            </div>
          )}

          <div className="flex flex-col">
            {(selectedEntity ? entities.filter(e => e.key === selectedEntity) : entities).map(
              entity => {
                const isSelected = selectedEntity === entity.key;
                return (
                  <button
                    key={entity.key}
                    onClick={() => {
                      if (isSelected) {
                        handleSearch();
                      } else {
                        handleEntitySelect(entity.key);
                      }
                    }}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-ds-default transition-colors text-left ${
                      isSelected
                        ? 'bg-primary/5 text-primary'
                        : 'text-ds-text-primary hover:bg-ds-bg'
                    }`}
                  >
                    <img
                      src={entity.icon}
                      alt=""
                      className="w-5 h-5 flex-shrink-0"
                      style={{
                        filter: isSelected
                          ? 'brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(2889%) hue-rotate(233deg) brightness(82%) contrast(97%)'
                          : 'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1051%) hue-rotate(191deg) brightness(92%) contrast(86%)',
                      }}
                    />
                    <span className="text-sm font-medium flex-1">{entity.label}</span>
                    {isSelected && query.trim() && (
                      <span className="text-xs text-primary/70">{t('pressEnterToSearch')}</span>
                    )}
                    {!isSelected && (
                      <img
                        src={SearchIcon}
                        alt=""
                        className="w-4 h-4 opacity-0 group-hover:opacity-100"
                        style={{
                          filter:
                            'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1051%) hue-rotate(191deg) brightness(92%) contrast(86%)',
                        }}
                      />
                    )}
                  </button>
                );
              }
            )}
          </div>
        </div>

        {/* Footer hint */}
        {selectedEntity && (
          <div className="px-4 py-2.5 border-t border-ds-border bg-ds-bg/50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-ds-text-tertiary">
                {query.trim()
                  ? t('searchResultsWillShow', {
                      entity: entities.find(e => e.key === selectedEntity)?.label,
                    })
                  : t('enterSearchTermOrBrowse')}
              </span>
              <button
                onClick={handleSearch}
                className="px-3 py-1 text-xs font-medium text-white bg-primary rounded-ds-default hover:bg-primary/90 transition-colors"
              >
                {query.trim() ? t('search') : t('browse')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
